import React, { useState, useEffect, useCallback } from "react";
import { getLeaveHistory, cancelLeave } from "../../../services/attendanceApi";
import { useToast } from "./Toast";
import Icons from "./Icons";

/**
 * LeaveHistory — list of the user's leave applications with status.
 * Allows cancel/withdraw for PENDING or APPROVED leaves.
 */
function LeaveHistory({ refreshKey, onCancelled }) {
  const { toast, confirm } = useToast();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getLeaveHistory();
      const data = res && res.data ? res.data : res;
      setLeaves(Array.isArray(data) ? data : (data.leaves || []));
    } catch (err) {
      setError(err.message || "Could not load leave history.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleCancel = async (id) => {
    const ok = await confirm("Cancel this leave request?", "Your leave balance will be restored.");
    if (!ok) return;
    try {
      setCancelling(id);
      await cancelLeave(id);
      await load();
      onCancelled && onCancelled();
      toast.success("Leave cancelled — balance restored");
    } catch (err) {
      toast.error(err.message || "Could not cancel leave");
    } finally {
      setCancelling(null);
    }
  };

  const statusClass = (s) => {
    switch ((s || "").toUpperCase()) {
      case "APPROVED": return "atl-pill atl-pill--present";
      case "PENDING": return "atl-pill atl-pill--late";
      case "REJECTED": return "atl-pill atl-pill--absent";
      case "CANCELLED": return "atl-pill";
      default: return "atl-pill";
    }
  };

  if (loading) {
    return <div className="atl-card atl-history"><div className="atl-skeleton" /></div>;
  }

  return (
    <div className="atl-card atl-history">
      <h3><span className="atl-h3ic">{Icons.history}</span>Leave History</h3>
      {error && <div className="atl-error">{error}</div>}
      {leaves.length === 0 ? (
        <div className="atl-empty">No leave applications yet.</div>
      ) : (
        <div className="atl-table-wrap">
          <table className="atl-table">
            <thead>
              <tr><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {leaves.map((l) => {
                const id = l.id;
                const status = (l.status || "").toUpperCase();
                const canCancel = status === "PENDING" || status === "APPROVED";
                const note = l.reviewNote || l.review_note;
                return (
                  <tr key={id}>
                    <td data-label="Type">{l.leaveType || l.leave_type}</td>
                    <td data-label="From">{fmtDate(l.fromDate || l.from_date)}</td>
                    <td data-label="To">{fmtDate(l.toDate || l.to_date)}</td>
                    <td data-label="Days">{l.totalDays || l.total_days}</td>
                    <td data-label="Status">
                      <span className={statusClass(status)}>{status}</span>
                      {note && <div className="atl-review-note" style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>{note}</div>}
                    </td>
                    <td data-label="">
                      {canCancel && (
                        <button className="atl-link-btn" onClick={() => handleCancel(id)} disabled={cancelling === id}>
                          {cancelling === id ? "..." : "Cancel"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short" });
}

export default LeaveHistory;
